import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  IncentiveBreakdown,
  IncentivePlan,
  IncentiveMarket,
  IncentivePlanRules,
  REPORTS_ENABLED,
  SalesTargetRow,
  createIncentivePlan,
  fetchIncentivePlans,
  fetchSalesTargets,
  previewIncentive,
  saveSalesTarget,
  updateIncentivePlan,
} from '../../../Reports/reportsApi';
import { Page, PageHeader, Card, Button, Badge, Field, Select, TextInput, Table, Loader, EmptyState } from '../../../ui';
import Tabs from './ui-pending/Tabs';
import './sales.scss';

/**
 * Sales → Targets & Incentives (SALES_ADMIN).
 *
 * What each sales agent must hit every week, and the incentive plan that
 * pays out on it. Targets and incentives are both weekly: Sunday to
 * Saturday, IST, a week named by its Sunday. The agents themselves are
 * managed on Sales → Sales Agents. Stored by artium-reports; the rules that
 * keep payouts honest are enforced there, and this page explains them where
 * they bite: past weeks are read-only, and a plan a past week used can't
 * have its rules changed (duplicate it instead).
 */

const num = (n: number): string => Math.round(n).toLocaleString('en-IN');
const inr = (n: number): string => `₹${num(n)}`;

// Weeks run Sunday–Saturday (IST) and are named by their Sunday, as in artium-reports.
const DAY = 864e5;
const parseIso = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const toIso = (d: Date): string => d.toISOString().slice(0, 10);
const istToday = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const weekStartOf = (iso: string): string => toIso(new Date(parseIso(iso).getTime() - parseIso(iso).getUTCDay() * DAY));
const shiftWeek = (weekStart: string, delta: number): string => toIso(new Date(parseIso(weekStart).getTime() + delta * 7 * DAY));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "4–10 Oct 2026", or "27 Sep – 3 Oct 2026" across a month. */
const weekLabel = (weekStart: string): string => {
  const a = parseIso(weekStart);
  const b = new Date(a.getTime() + 6 * DAY);
  return a.getUTCMonth() === b.getUTCMonth()
    ? `${a.getUTCDate()}–${b.getUTCDate()} ${MONTHS[b.getUTCMonth()]} ${b.getUTCFullYear()}`
    : `${a.getUTCDate()} ${MONTHS[a.getUTCMonth()]} – ${b.getUTCDate()} ${MONTHS[b.getUTCMonth()]} ${b.getUTCFullYear()}`;
};

const errMsg = (err: any, fallback: string): string => err?.message || fallback;

// ===========================================================================
// Targets
// ===========================================================================

/** An agent's week: the plan they're on (its gates are the targets) and an optional demo target. */
type Draft = { demoTarget: string; incentivePlanId: string };

const draftOf = (row: SalesTargetRow): Draft => ({
  demoTarget: row.target?.demoTarget != null ? String(row.target.demoTarget) : '',
  incentivePlanId: row.target?.incentivePlan?.id ?? '',
});

const blankToNull = (v: string): number | null => (v.trim() === '' ? null : Math.round(Number(v)));

const TargetsTab: React.FC<{ plans: IncentivePlan[] }> = ({ plans }) => {
  const current = weekStartOf(istToday());
  const [week, setWeek] = useState(current);
  const [rows, setRows] = useState<SalesTargetRow[]>([]);
  const [locked, setLocked] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSalesTargets(week);
      setRows(data.rows);
      setLocked(data.locked);
      setDrafts(Object.fromEntries(data.rows.map((r) => [r.agent.id, draftOf(r)])));
    } catch (err) {
      toast.error(errMsg(err, 'Could not load targets'));
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => { void load(); }, [load]);

  const dirty = rows.filter((r) => JSON.stringify(drafts[r.agent.id]) !== JSON.stringify(draftOf(r)));
  const assignablePlans = plans.filter((p) => p.isActive);
  /** The plan picked in a row's draft (archived ones included, so their minimums still show). */
  const planFor = (d: Draft) => plans.find((p) => p.id === d.incentivePlanId);


  const setField = (agentId: string, field: keyof Draft, value: string) =>
    setDrafts((prev) => ({ ...prev, [agentId]: { ...prev[agentId], [field]: value } }));

  const saveAll = async () => {
    setSaving(true);
    let ok = 0;
    for (const r of dirty) {
      const d = drafts[r.agent.id];
      try {
        await saveSalesTarget(r.agent.id, week, {
          demoTarget: blankToNull(d.demoTarget),
          incentivePlanId: d.incentivePlanId || null,
        });
        ok++;
      } catch (err) {
        toast.error(`${r.agent.name}: ${errMsg(err, 'not saved')}`);
      }
    }
    setSaving(false);
    if (ok) toast.success(`Saved ${ok} target${ok === 1 ? '' : 's'} for ${weekLabel(week)}`);
    void load();
  };

  const totals = rows.reduce(
    (t, r) => {
      const d = drafts[r.agent.id];
      if (!d) return t;
      return {
        onPlan: t.onPlan + (d.incentivePlanId ? 1 : 0),
        demos: t.demos + (blankToNull(d.demoTarget) ?? 0),
      };
    },
    { onPlan: 0, demos: 0 },
  );

  const weekPicker = (
    <div className="sales-actions sales-actions--center">
      <div className="sales-week" role="group" aria-label="Week (Sun–Sat)">
        <Button variant="secondary" size="sm" title="Previous week" aria-label="Previous week"
          disabled={dirty.length > 0} onClick={() => setWeek(shiftWeek(week, -1))}>‹</Button>
        <span className="sales-week__label">
          {weekLabel(week)}{week === current ? ' · this week' : ''}
        </span>
        <Button variant="secondary" size="sm" title="Next week" aria-label="Next week"
          disabled={dirty.length > 0} onClick={() => setWeek(shiftWeek(week, 1))}>›</Button>
      </div>
      {week !== current && (
        <Button variant="ghost" size="sm" disabled={dirty.length > 0} onClick={() => setWeek(current)}>This week</Button>
      )}
      {!locked && (
        <Button variant="primary" size="sm" loading={saving} disabled={!dirty.length} onClick={() => void saveAll()}>
          {`Save ${dirty.length || ''} change${dirty.length === 1 ? '' : 's'}`}
        </Button>
      )}
    </div>
  );

  return (
    <Card
      flush
      title="Weekly targets"
      subtitle={
        <>
          The week of {weekLabel(week)} (Sunday–Saturday). Each agent&apos;s incentive plan sets their targets: its gates are the
          revenue and conversions they must hit. <span className="ds-strong">Plans carry forward</span> week to week on their own; change one only when
          something changes, and it applies from this week on.
        </>
      }
      actions={weekPicker}
    >
      {locked && (
        <div className="sales-callout">
          The week of {weekLabel(week)} is over, so its targets are history and read-only.
        </div>
      )}

      {loading ? (
        <Loader label="Loading targets…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No sales agents yet"
          description={<>Add them on <Link to={`${process.env.PUBLIC_URL}/pages/sales-agents`}>Sales Agents</Link> first.</>}
        />
      ) : (
        <Table caption={`Weekly targets for ${weekLabel(week)}`}>
          <thead>
            <tr>
              <th>Agent</th>
              <th>Region</th>
              <th>Incentive plan</th>
              <th>Targets this week (from the plan)</th>
              <th className="ds-num" title="Optional; no plan sets a demo target">Demo target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = drafts[r.agent.id] ?? draftOf(r);
              const changed = JSON.stringify(d) !== JSON.stringify(draftOf(r));
              const plan = planFor(d);
              const planOptions = [
                { value: '', label: 'No incentive plan' },
                // Keep showing an archived plan already on this target.
                ...(r.target?.incentivePlan && !assignablePlans.some((p) => p.id === r.target!.incentivePlan!.id)
                  ? [{ value: r.target.incentivePlan.id, label: `${r.target.incentivePlan.name} (archived)` }]
                  : []),
                ...assignablePlans.map((p) => ({ value: p.id, label: p.name })),
              ];
              return (
                <tr key={r.agent.id} className={changed ? 'sales-row--changed' : undefined}>
                  <td className="sales-nowrap">
                    <div className="ds-strong">{r.agent.name} {!r.agent.isActive && <Badge tone="neutral">inactive</Badge>}</div>
                    <div className="sales-caption ds-text-muted">{r.agent.email}</div>
                  </td>
                  <td>{r.agent.region ?? '—'}</td>
                  <td>
                    <Select size="sm" className="sales-plan-select" disabled={locked} value={d.incentivePlanId}
                      aria-label={`Incentive plan for ${r.agent.name}`} options={planOptions}
                      onChange={(e) => setField(r.agent.id, 'incentivePlanId', e.target.value)} />
                  </td>
                  <td className="sales-caption">
                    {plan ? gatesText(plan) : <span className="ds-text-muted">No plan</span>}
                    {r.target?.inheritedFrom && !changed && (
                      <div className="ds-text-muted" title="No change since then; it carries forward automatically">
                        ↻ carried forward from {weekLabel(r.target.inheritedFrom)}
                      </div>
                    )}
                    {changed && !locked && (
                      <div className="sales-text--warning">Applies from this week on</div>
                    )}
                  </td>
                  <td>
                    <TextInput type="number" min={0} size="sm" numeric className="sales-num-input sales-num-input--end" disabled={locked}
                      value={d.demoTarget} placeholder="optional" aria-label={`Demo target for ${r.agent.name}`}
                      onChange={(e) => setField(r.agent.id, 'demoTarget', e.target.value)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Team</td>
              <td className="ds-text-muted">{totals.onPlan} of {rows.length} agents on a plan</td>
              <td className="ds-num">{num(totals.demos)} demos</td>
            </tr>
          </tfoot>
        </Table>
      )}
    </Card>
  );
};

// ===========================================================================
// Incentive plans (weekly)
// ===========================================================================

const COUNTRIES = ['India', 'USA', 'ROW'] as const;
type Country = (typeof COUNTRIES)[number];

/** Form state: every number is a string while it's being typed. */
type PlanForm = {
  id: string | null;
  name: string;
  description: string;
  market: IncentiveMarket;
  isActive: boolean;
  minRevenue: string;
  minConversions: string;
  gates: { country: Country; minPct: string }[];
  conversionPayoutFrom: string;
  conversionPayoutBase: string;
  conversionPayoutPerExtra: string;
  revenuePct: string;
  tiers: { minSessions: string; label: string; amount: string }[];
  lockedReason: string | null;
};

const DEFAULT_TIERS = [
  { minSessions: '36', label: 'Silver 🥈', amount: '500' },
  { minSessions: '48', label: 'Gold 🥇', amount: '1500' },
  { minSessions: '72', label: 'Gold+ 🥇🥇', amount: '2000' },
  { minSessions: '96', label: 'Platinum 💎', amount: '4000' },
  { minSessions: '120', label: 'Platinum+ 💎💎', amount: '5000' },
];

const NEW_PLAN: PlanForm = {
  id: null,
  name: '',
  description: '',
  market: 'DOMESTIC',
  isActive: true,
  minRevenue: '175000',
  minConversions: '15',
  gates: [{ country: 'India', minPct: '20' }],
  conversionPayoutFrom: '15',
  conversionPayoutBase: '2400',
  conversionPayoutPerExtra: '100',
  revenuePct: '1',
  tiers: DEFAULT_TIERS,
  lockedReason: null,
};

const formOf = (p: IncentivePlan, copy = false): PlanForm => ({
  id: copy ? null : p.id,
  name: copy ? `${p.name} (copy)` : p.name,
  description: p.description ?? '',
  market: p.market,
  isActive: copy ? true : p.isActive,
  minRevenue: String(p.minRevenue),
  minConversions: String(p.minConversions),
  gates: p.conversionPctGates.map((g) => ({ country: g.country, minPct: String(g.minPct) })),
  conversionPayoutFrom: String(p.conversionPayoutFrom),
  conversionPayoutBase: String(p.conversionPayoutBase),
  conversionPayoutPerExtra: String(p.conversionPayoutPerExtra),
  revenuePct: String(p.revenuePct),
  tiers: p.sessionTiers.map((t) => ({ minSessions: String(t.minSessions), label: t.label, amount: String(t.amount) })),
  lockedReason: copy ? null : p.lockedReason,
});

const int = (v: string): number => Math.round(Number(v) || 0);

const rulesOf = (f: PlanForm): IncentivePlanRules => ({
  minRevenue: int(f.minRevenue),
  minConversions: int(f.minConversions),
  conversionPctGates: f.gates.map((g) => ({ country: g.country, minPct: Number(g.minPct) || 0 })),
  conversionPayoutFrom: int(f.conversionPayoutFrom),
  conversionPayoutBase: int(f.conversionPayoutBase),
  conversionPayoutPerExtra: int(f.conversionPayoutPerExtra),
  revenuePct: Number(f.revenuePct) || 0,
  sessionTiers: f.tiers
    .map((t) => ({ minSessions: int(t.minSessions), label: t.label.trim(), amount: int(t.amount) }))
    .sort((a, b) => a.minSessions - b.minSessions),
});

const lakh = (n: number): string => (n >= 100000 ? `₹${(n / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })}L` : inr(n));

const gatesText = (p: IncentivePlanRules): string =>
  [
    `${lakh(p.minRevenue)} revenue`,
    `${p.minConversions}+ conversions`,
    ...p.conversionPctGates.map((g) => `${g.country} ≥ ${g.minPct}%`),
  ].join(' · ');

const payoutText = (p: IncentivePlanRules): string =>
  `${inr(p.conversionPayoutBase)} at ${p.conversionPayoutFrom}, +${inr(p.conversionPayoutPerExtra)} each after`;

/** "What would this plan pay for a week like this?" — every number editable. */
const Calculator: React.FC<{ rules: IncentivePlanRules }> = ({ rules }) => {
  const [revenue, setRevenue] = useState('200000');
  const [conversions, setConversions] = useState('16');
  const [byCountry, setByCountry] = useState<Record<Country, { assigned: string; converted: string }>>({
    India: { assigned: '70', converted: '16' },
    USA: { assigned: '20', converted: '9' },
    ROW: { assigned: '15', converted: '5' },
  });
  const [sales, setSales] = useState<Record<string, string>>({});
  const [result, setResult] = useState<IncentiveBreakdown | null>(null);
  const [error, setError] = useState('');
  const rulesKey = JSON.stringify(rules);
  const gateCountries = rules.conversionPctGates.map((g) => g.country);
  const salesKey = JSON.stringify(sales);
  const countryKey = JSON.stringify(byCountry);

  useEffect(() => {
    const t = setTimeout(() => {
      const r: IncentivePlanRules = JSON.parse(rulesKey);
      const saleSessions = r.sessionTiers.flatMap((tier) => Array(Math.max(0, int(sales[tier.minSessions] ?? '0'))).fill(tier.minSessions));
      const countries = Object.fromEntries(
        r.conversionPctGates.map((g) => [g.country, { assigned: int(byCountry[g.country].assigned), converted: int(byCountry[g.country].converted) }]),
      );
      previewIncentive(r, { revenue: Number(revenue) || 0, conversions: int(conversions), byCountry: countries, saleSessions })
        .then((res) => { setResult(res); setError(''); })
        .catch((err) => { setResult(null); setError(errMsg(err, 'Check the plan')); });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesKey, revenue, conversions, countryKey, salesKey]);

  const small = (value: string, onChange: (v: string) => void, label: string) => (
    <TextInput type="number" min={0} size="sm" numeric className="sales-num-input" value={value} aria-label={label}
      onChange={(e) => onChange(e.target.value)} />
  );
  const labelled = (label: string, value: string, onChange: (v: string) => void) => (
    <Field label={label} className="sales-num-field">
      {(ctl) => (
        <TextInput {...ctl} type="number" min={0} size="sm" numeric value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </Field>
  );
  const gateTone = (g: { skipped?: boolean; met: boolean }) =>
    g.skipped ? 'ds-text-subtle' : g.met ? 'sales-text--success' : 'sales-text--danger';

  return (
    <div className="sales-calculator">
      <h3 className="sales-subheading">Try it: what would one week pay?</h3>
      <div className="sales-form-row">
        {labelled('Revenue ₹', revenue, setRevenue)}
        {labelled('Conversions', conversions, setConversions)}
      </div>
      {gateCountries.length > 0 && (
        <Table caption="Leads by country">
          <thead><tr><th>Leads from</th><th>Assigned</th><th>Converted</th></tr></thead>
          <tbody>
            {gateCountries.map((c) => (
              <tr key={c}>
                <td>{c}</td>
                <td>{small(byCountry[c].assigned, (v) => setByCountry({ ...byCountry, [c]: { ...byCountry[c], assigned: v } }), `${c} assigned`)}</td>
                <td>{small(byCountry[c].converted, (v) => setByCountry({ ...byCountry, [c]: { ...byCountry[c], converted: v } }), `${c} converted`)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {rules.sessionTiers.length > 0 && (
        <div>
          <div className="sales-caption ds-text-muted">Sales this week, by package</div>
          <div className="sales-form-row">
            {rules.sessionTiers.map((t) => (
              <React.Fragment key={t.minSessions}>
                {labelled(`${t.minSessions} sessions`, sales[t.minSessions] ?? '0', (v) => setSales({ ...sales, [t.minSessions]: v }))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {error ? (
        <div className="sales-text--danger">{error}</div>
      ) : result && (
        <>
          <div>
            {result.gates.map((g) => (
              <div key={g.key} className={`sales-line ${gateTone(g)}`}>
                <span>{g.skipped ? '–' : g.met ? '✓' : '✗'} {g.label} ≥ {g.key === 'revenue' ? inr(g.required) : g.key.startsWith('pct:') ? `${g.required}%` : g.required}</span>
                <span>{g.skipped ? 'no leads, not applied' : g.key === 'revenue' ? inr(g.actual ?? 0) : g.key.startsWith('pct:') ? `${g.actual}%` : g.actual}</span>
              </div>
            ))}
          </div>
          <div className="sales-calculator__totals">
            <div className="sales-line"><span>Conversion payout</span><span className="ds-strong ds-num">{inr(result.eligible ? result.conversionPayout : 0)}</span></div>
            <div className="sales-line"><span>{rules.revenuePct}% of revenue</span><span className="ds-strong ds-num">{inr(result.eligible ? result.revenuePayout : 0)}</span></div>
            <div className="sales-line"><span>Session bonus{result.sessionBonusByTier.length ? ` · ${result.sessionBonusByTier.map((t) => `${t.sales}× ${t.label}`).join(', ')}` : ''}</span><span className="ds-strong ds-num">{inr(result.eligible ? result.sessionBonus : 0)}</span></div>
            <div className="sales-line sales-line--total">
              <span className="ds-strong">Weekly incentive</span>
              <span className={`ds-strong ds-num ${result.eligible ? 'sales-text--success' : 'sales-text--danger'}`}>{inr(result.total)}</span>
            </div>
            {!result.eligible && result.potentialTotal > 0 && (
              <div className="sales-caption sales-text--danger">
                A gate isn&apos;t met, so nothing is paid. Meeting every gate would pay {inr(result.potentialTotal)}.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const PlansTab: React.FC<{ plans: IncentivePlan[]; reload: () => void }> = ({ plans, reload }) => {
  const [form, setForm] = useState<PlanForm | null>(null);
  const [saving, setSaving] = useState(false);
  const locked = Boolean(form?.lockedReason);

  const submit = async () => {
    if (!form) return;
    const body = { name: form.name, description: form.description || null, market: form.market, isActive: form.isActive, ...rulesOf(form) };
    setSaving(true);
    try {
      if (form.id) await updateIncentivePlan(form.id, body);
      else await createIncentivePlan(body);
      toast.success(`Saved "${form.name}"`);
      setForm(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err, 'Could not save the plan'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p: IncentivePlan) => {
    try {
      await updateIncentivePlan(p.id, {
        name: p.name,
        description: p.description,
        market: p.market,
        isActive: !p.isActive,
        minRevenue: p.minRevenue,
        minConversions: p.minConversions,
        conversionPctGates: p.conversionPctGates,
        conversionPayoutFrom: p.conversionPayoutFrom,
        conversionPayoutBase: p.conversionPayoutBase,
        conversionPayoutPerExtra: p.conversionPayoutPerExtra,
        revenuePct: p.revenuePct,
        sessionTiers: p.sessionTiers,
      });
      reload();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update the plan'));
    }
  };

  if (form) {
    const field = (label: string, key: keyof PlanForm, hint?: string) => (
      <Field label={label} hint={hint} className="sales-num-field">
        {(ctl) => (
          <TextInput {...ctl} type="number" min={0} size="sm" numeric disabled={locked} value={form[key] as string}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
        )}
      </Field>
    );
    const unusedCountries = COUNTRIES.filter((c) => !form.gates.some((g) => g.country === c));
    const from = int(form.conversionPayoutFrom);
    const base = int(form.conversionPayoutBase);
    const extra = int(form.conversionPayoutPerExtra);

    return (
      <Card
        title={form.id ? 'Edit plan' : 'New incentive plan'}
        subtitle="Paid per week (Sunday–Saturday). If any gate isn't met, nothing is paid that week."
        actions={<Button variant="secondary" size="sm" onClick={() => setForm(null)}>Back to plans</Button>}
      >
        {locked && (
          <div className="sales-callout sales-callout--warning">
            {form.lockedReason}, so its rules are history. You can rename or archive it; to change the rules,
            duplicate it and give the copy to future targets.
          </div>
        )}
        <div className="row g-4">
          <div className="col-12 col-xl-7">
            <div className="sales-form">
              <div className="sales-form-row">
                <Field label="Plan name" className="sales-form-row__grow">
                  {(ctl) => (
                    <TextInput {...ctl} size="sm" placeholder="Plan name, e.g. Domestic weekly incentive" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  )}
                </Field>
                <Field label="Market">
                  {(ctl) => (
                    <Select {...ctl} size="sm" value={form.market}
                      options={[{ value: 'DOMESTIC', label: 'Domestic' }, { value: 'INTERNATIONAL', label: 'International' }]}
                      onChange={(e) => setForm({ ...form, market: e.target.value as IncentiveMarket })} />
                  )}
                </Field>
              </div>
              <Field label="Description">
                {(ctl) => (
                  <textarea {...ctl} rows={2} className="ds-input sales-textarea" placeholder="Description (optional)" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                )}
              </Field>

              <h3 className="sales-subheading">1 · Gates — all must be met in the week</h3>
              <div className="sales-form-row">
                {field('Min revenue ₹', 'minRevenue', lakh(int(form.minRevenue)))}
                {field('Min conversions', 'minConversions')}
              </div>
              <div className="sales-caption ds-text-muted">Min conversion % by lead country (not applied in a week with no leads from it)</div>
              {form.gates.map((g, i) => (
                <div key={g.country} className="sales-inline">
                  <span className="sales-inline__label">{g.country}</span>
                  <TextInput type="number" min={0} max={100} size="sm" numeric className="sales-num-input" disabled={locked} value={g.minPct}
                    aria-label={`Min conversion % for ${g.country}`}
                    onChange={(e) => setForm({ ...form, gates: form.gates.map((x, j) => (j === i ? { ...x, minPct: e.target.value } : x)) })} />
                  <span>%</span>
                  {!locked && (
                    <Button variant="ghost" size="sm" className="sales-remove"
                      onClick={() => setForm({ ...form, gates: form.gates.filter((_, j) => j !== i) })}>Remove</Button>
                  )}
                </div>
              ))}
              {!locked && unusedCountries.length > 0 && (
                <div className="sales-actions">
                  {unusedCountries.map((c) => (
                    <Button key={c} variant="secondary" size="sm"
                      onClick={() => setForm({ ...form, gates: [...form.gates, { country: c, minPct: '' }] })}>+ {c} %</Button>
                  ))}
                </div>
              )}

              <h3 className="sales-subheading">2 · Conversion payout</h3>
              <div className="sales-form-row">
                {field('Starts at conversion #', 'conversionPayoutFrom')}
                {field('Pays ₹ at that conversion', 'conversionPayoutBase')}
                {field('+ ₹ per conversion after', 'conversionPayoutPerExtra')}
              </div>
              <div className="sales-caption ds-text-muted">
                {from} conversions → {inr(base)}, {from + 1} → {inr(base + extra)}, {from + 5} → {inr(base + 5 * extra)}
                {int(form.minConversions) < from && <>; {form.minConversions}–{from - 1} conversions pass the gate but earn no conversion payout</>}.
              </div>

              <h3 className="sales-subheading">3 · Revenue payout</h3>
              <div className="sales-inline">
                <TextInput type="number" min={0} max={100} step="0.1" size="sm" numeric className="sales-num-input" disabled={locked} value={form.revenuePct}
                  aria-label="Revenue payout %"
                  onChange={(e) => setForm({ ...form, revenuePct: e.target.value })} />
                <span>% of the week&apos;s revenue</span>
              </div>

              <h3 className="sales-subheading">4 · Session bonus — per sale, by package size</h3>
              <Table caption="Session bonus tiers" className="sales-tier-table">
                <thead><tr><th>Package of at least</th><th>Tier</th><th>₹ per sale</th><th><span className="visually-hidden">Actions</span></th></tr></thead>
                <tbody>
                  {form.tiers.map((t, i) => {
                    const set = (key: 'minSessions' | 'label' | 'amount', v: string) =>
                      setForm({ ...form, tiers: form.tiers.map((x, j) => (j === i ? { ...x, [key]: v } : x)) });
                    return (
                      <tr key={i}>
                        <td><div className="sales-inline">
                          <TextInput type="number" min={1} size="sm" numeric className="sales-num-input" disabled={locked} value={t.minSessions}
                            aria-label={`Tier ${i + 1} minimum sessions`} onChange={(e) => set('minSessions', e.target.value)} />
                          <span className="ds-text-muted">sessions</span></div></td>
                        <td><TextInput size="sm" disabled={locked} value={t.label} aria-label={`Tier ${i + 1} name`} onChange={(e) => set('label', e.target.value)} /></td>
                        <td><TextInput type="number" min={0} size="sm" numeric disabled={locked} value={t.amount} aria-label={`Tier ${i + 1} ₹ per sale`} onChange={(e) => set('amount', e.target.value)} /></td>
                        <td>{!locked && (
                          <Button variant="ghost" size="sm" className="sales-remove" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, j) => j !== i) })}>Remove</Button>
                        )}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {!locked && (
                <div className="sales-actions">
                  <Button variant="secondary" size="sm"
                    onClick={() => setForm({ ...form, tiers: [...form.tiers, { minSessions: '', label: '', amount: '' }] })}>+ Add tier</Button>
                </div>
              )}

              <div className="sales-actions sales-actions--center">
                <Button variant="primary" loading={saving} disabled={!form.name.trim()} onClick={() => void submit()}>
                  Save plan
                </Button>
                <label className="sales-check">
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active (can be given to targets)
                </label>
              </div>
            </div>
          </div>
          <div className="col-12 col-xl-5">
            <Calculator rules={rulesOf(form)} />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      flush
      title="Incentive plans"
      subtitle="Weekly (Sunday–Saturday). Every gate must be met; then a conversion payout, a % of revenue and a bonus per sale by package size. Give a plan to agents on the Targets tab."
      actions={<Button variant="primary" size="sm" onClick={() => setForm(NEW_PLAN)}>+ New plan</Button>}
    >
      {plans.length === 0 ? (
        <EmptyState title="No plans yet." description="Create one with + New plan." />
      ) : (
        <Table caption="Incentive plans">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Gates (all required)</th>
              <th>Conversion payout</th>
              <th>Revenue</th>
              <th>Session bonus</th>
              <th>Used in</th>
              <th><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className={p.isActive ? undefined : 'sales-row--inactive'}>
                <td>
                  <div className="ds-strong">{p.name} {!p.isActive && <Badge tone="neutral">archived</Badge>}</div>
                  <Badge tone="neutral">{p.market === 'DOMESTIC' ? 'Domestic' : 'International'}</Badge>
                </td>
                <td>{gatesText(p)}</td>
                <td>{payoutText(p)}</td>
                <td className="ds-num">{p.revenuePct}%</td>
                <td className="sales-caption">{p.sessionTiers.map((t) => `${t.minSessions}→${inr(t.amount)}`).join(' · ') || '—'}</td>
                <td className="sales-caption">
                  {p.usedInWeeks.length ? p.usedInWeeks.slice(0, 2).map(weekLabel).join(', ') + (p.usedInWeeks.length > 2 ? ` +${p.usedInWeeks.length - 2} more` : '') : <span className="ds-text-muted">—</span>}
                  {p.lockedReason && <div><span title="Rules are frozen"><Badge tone="neutral">🔒 rules frozen</Badge></span></div>}
                </td>
                <td>
                  <div className="sales-actions sales-actions--end">
                    <Button variant="ghost" size="sm" onClick={() => setForm(formOf(p))}>{p.lockedReason ? 'View' : 'Edit'}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setForm(formOf(p, true))}>Duplicate</Button>
                    <Button variant="ghost" size="sm" onClick={() => void toggleActive(p)}>{p.isActive ? 'Archive' : 'Restore'}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
};

// ===========================================================================

const TABS = [
  { key: 'targets', label: 'Targets' },
  { key: 'plans', label: 'Incentive plans' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const SalesTargetsPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('targets');
  const [plans, setPlans] = useState<IncentivePlan[]>([]);

  const loadPlans = useCallback(() => {
    fetchIncentivePlans().then(setPlans).catch((err) => toast.error(errMsg(err, 'Could not load incentive plans')));
  }, []);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  if (!REPORTS_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="Sales" title="Targets & Incentives" />
        <EmptyState title="Reports aren't enabled" description="Reports aren't enabled in this environment yet." />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Sales"
        title="Targets & Incentives"
        subtitle={
          <>
            What each sales agent must hit every week (Sunday–Saturday), and how they&apos;re paid for it.{' '}
            <Link to={`${process.env.PUBLIC_URL}/pages/sales-agents`}>Manage agents</Link>
          </>
        }
      />

      <Tabs
        label="Targets and incentives"
        tabs={TABS.map((t) => ({ key: t.key, label: t.label }))}
        active={tab}
        onChange={(key) => { setTab(key); loadPlans(); }}
      />

      {tab === 'targets' && <TargetsTab plans={plans} />}
      {tab === 'plans' && <PlansTab plans={plans} reload={loadPlans} />}
    </Page>
  );
};

export default SalesTargetsPage;
